import { Backdrop, CircularProgress } from '@mui/material';
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LoadingContextType {
  showLoader: () => void;
  hideLoader: () => void;
  isLoading: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  // Simple counter to handle overlapping requests
  // (e.g., if two API calls start, we only hide loader when BOTH finish)
  const [_loadingCounter, setLoadingCounter] = useState(0);

  const showLoader = () => {
    setLoadingCounter((prev) => prev + 1);
    setIsLoading(true);
  };

  const hideLoader = () => {
    setLoadingCounter((prev) => {
      const newCount = Math.max(0, prev - 1);
      if (newCount === 0) setIsLoading(false);
      return newCount;
    });
  };

  return (
    <LoadingContext.Provider value={{ showLoader, hideLoader, isLoading }}>
      {children}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 9999, // Above everything
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </LoadingContext.Provider>
  );
};

// Custom Hook
export const useLoader = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoader must be used within a LoadingProvider');
  }
  return context;
};